import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Package, Calendar, Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

export default function ReportsProductsPage() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [productData, setProductData] = useState(null);
  const [chartKey, setChartKey] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  useEffect(() => {
    setTablePage(1);
  }, [period]);

  useEffect(() => {
    fetchProductReport();
  }, [period, tablePage]);

  useEffect(() => {
    setChartKey((prev) => prev + 1);
  }, [productData]);

  const fetchProductReport = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getProductSales(period, {
        table_page: tablePage,
        table_page_size: tablePageSize,
      });
      setProductData(response.data);
      setTableTotal(response.data.product_sales_total || 0);
    } catch (error) {
      toast.error('Failed to fetch product sales report');
    } finally {
      setLoading(false);
    }
  };

  const productChartData = useMemo(
    () => (productData?.product_sales_chart || productData?.product_sales || []).map((product) => ({
      ...product,
      total_amount: Number(product.total_amount || 0),
      total_pieces: Number(product.total_pieces || 0),
      order_count: Number(product.order_count || 0),
    })),
    [productData]
  );

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await adminApi.downloadAdminDatasetExcel('product-sales', { period });
      downloadBlob(res.data, 'product-sales.xlsx');
    } catch (error) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-products-page">
      <LoadingOverlay open={downloadingExcel} message="Please wait. Report is being generated" />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Reports · Product Sales</h1>
          <p className="text-[#A8A8A8] mt-1">Top products and revenue distribution</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDownloadExcel}>
            <Download className="w-4 h-4 mr-2" /> Download Excel
          </Button>
          <Calendar className="w-5 h-5 text-[#A8A8A8]" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 border-[#E5E5E5]" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Products Sold</p>
            <p className="text-2xl font-bold text-[#2D2420]">{productData?.total_products_sold || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Total Revenue</p>
            <p className="text-2xl font-bold text-[#4A7c59]">{formatCurrency(productData?.total_revenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Top Product</p>
            <p className="text-lg font-bold text-[#C97A5E] truncate">{productData?.product_sales_chart?.[0]?.product_name || productData?.product_sales?.[0]?.product_name || '-'}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Product Sales Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart key={`products-${chartKey}`} data={productChartData.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <YAxis dataKey="product_name" type="category" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="total_amount" fill="#C97A5E" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Product-wise Sales</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Product</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Orders</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Pieces</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {productData?.product_sales?.map((product, idx) => (
                  <tr key={idx} className="hover:bg-[#FAFAF9]/50">
                    <td className="py-3 px-4 font-medium text-[#2D2420]">{product.product_name}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{product.order_count}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{product.total_pieces}</td>
                    <td className="py-3 px-4 text-[#4A7c59] font-semibold">{formatCurrency(product.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
        {tableTotal > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-4 border-t border-[#E5E5E5]">
            <p className="text-sm text-[#A8A8A8]">
              Showing {(tablePage - 1) * tablePageSize + 1} to {Math.min(tablePage * tablePageSize, tableTotal)} of {tableTotal}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault();
                      setTablePage((prev) => Math.max(1, prev - 1));
                    }}
                    className={tablePage === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-[#5C4033]">
                    Page {tablePage} of {Math.max(1, Math.ceil(tableTotal / tablePageSize))}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault();
                      setTablePage((prev) => Math.min(Math.max(1, Math.ceil(tableTotal / tablePageSize)), prev + 1));
                    }}
                    className={tablePage >= Math.max(1, Math.ceil(tableTotal / tablePageSize)) ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>
    </div>
  );
}
